import { plugin, debug } from 'light-component';
import dayjs from 'dayjs';
import { useEffect, useState, type ReactElement } from 'react';
import * as Base from '@base';
import { t } from '@common';
import { Require } from '../component';
import styles from './style.module.less';
import type { Dayjs } from 'dayjs';
import type { DateRangeProps } from './types';

type RangeValue<T> = [T | null, T | null] | null;
export function WidgetDateRange(props: DateRangeProps): ReactElement {
  debug.widget('dashboard date range', props);
  const { allowClear = true } = props;
  const [dates, setDates] = useState<[(Dayjs | null), (Dayjs | null)]>([null, null]);
  const [maxDate, setMaxDate] = useState<Dayjs>();

  useEffect(() => {
    if (props.maxDate == null) {
      return;
    }
    props.maxDate === '$now' ? setMaxDate(dayjs()) : setMaxDate(dayjs(props.maxDate));
  }, [props.maxDate]);

  useEffect(() => {
    const [start, end] = props.value ?? [null, null];
    const startDate = typeof (start) === 'string' ? dayjs(start) : start;
    const endDate = typeof (end) === 'string' ? dayjs(end) : end;

    if (startDate != null && endDate != null && startDate.isAfter(endDate)) {
      setDates([endDate, startDate]);
      return;
    }
    setDates([startDate, endDate]);
  }, [props.value]);

  const getYearMonth = (date: Dayjs): number => (date.year() * 12) + date.month();

  const disabledDate: Base.DatePickerProps['disabledDate'] = (current, { from, type }) => {
    if (from != null) {
      const minDate = from.add(-props.maxRange + 1, 'days');
      const maxDate = from.add(props.maxRange - 1, 'days');

      switch (type) {
        case 'year':
          return current.year() < minDate.year() || current.year() > maxDate.year();

        case 'month':
          return (
            getYearMonth(current) < getYearMonth(minDate)
            || getYearMonth(current) > getYearMonth(maxDate)
          );

        default:
          return Math.abs(current.diff(from, 'days')) >= props.maxRange;
      }
    }

    return false;
  };

  // 监听日期选择器的日历变化
  function onChange(dates: RangeValue<Dayjs>): void {
    const [start, end] = dates ?? [null, null];
    const startDate = start != null ? dayjs(start).format('YYYY-MM-DD') : start;
    const endDate = end != null ? dayjs(end).format('YYYY-MM-DD') : end;
    props.onChange?.([startDate, endDate]);
  }

  return (
    <div className={props.className}>
      <div className={styles.input}>
        <Require require={props.require} />
        <span className={styles.title}>{t(props.title ?? '')}</span>
      </div>
      <Base.DatePicker.RangePicker
        className={styles.treeSelect}
        placeholder={[t('开始时间'), t('结束时间')]}
        allowEmpty
        allowClear={allowClear}
        disabled={props.disabled}
        value={dates}
        disabledDate={disabledDate}
        onChange={onChange}
        onPanelChange={onChange}
        maxDate={maxDate}
      />
    </div>
  );
}

plugin.register({ kind: 'widget', type: 'daterange', component: WidgetDateRange });
